import {
  CHARGEBACK_STATE,
  EXTRA_DETAILS_TYPE,
  OPERATION_STATE,
  TRANSACTIONS_STATE,
  TRANSACTIONS_TYPE,
} from 'api/transactions/constants';
import {
  ICascading,
  ICategorizedRuleset,
  IChargebackStatistics,
  IExtraDetails,
  IOperationHistory,
  IRoutingHistory,
  IRoutingHistoryData,
  IRuleset,
  ITransaction,
  ITransactionCard,
  ITransactionCascading,
  ITransactionCascadingData,
  ITransactionCustomer,
  ITransactionDetails,
  ITransactionDetailsResponse,
  ITransactionOperationHistory,
  ITransactionOperationHistoryData,
  ITransactionRoutingHistory,
  ITransactionRoutingHistoryData,
  ITransactionWebhooks,
  ITransactionWebhooksData,
  IWebhook,
} from 'api/transactions/types';
import { INullable, TObject } from 'utils/types';

export const normalizeTransaction = (data: TObject): ITransaction => {
  return {
    adapterCode: (data?.adapterCode as string) || '',
    amount: (data?.amount as number) || 0,
    antifraudProvider: (data?.antifraudProvider as string) || 'NONE',
    bankName: (data?.bankName as string) || '',
    bankResultCode: (data?.bankResultCode as string) || '',
    bankTerminal: (data?.bankTerminal as string) || '',
    bankTID: (data?.bankTID as string) || '',
    baseCurrency: (data?.baseCurrency as string) || '',
    baseCurrencyAmount: data?.baseCurrencyAmount as number,
    binCountry: (data?.binCountry as string) || '',
    binCountryCode: (data?.binCountryCode as string) || '',
    browserName: (data?.browserName as string) || '',
    browserVersion: (data?.browserVersion as string) || '',
    card: (data?.card as string) || '',
    cardBrand: (data?.cardBrand as string) || '',
    cardCategory: (data?.cardCategory as string) || '',
    cardFullBin: (data?.cardFullBin as string) || '',
    cardFullBinLength: (data?.cardFullBinLength as number) || null,
    cardholder: (data?.cardholder as string) || '',
    cardIssuingOrganization: (data?.cardIssuingOrganization as string) || '',
    cardType: (data?.cardType as string) || '',
    cascadingHistoryOrder: (data?.cascadingHistoryOrder as number) || null,
    cascadingSchemaName: (data?.cascadingSchemaName as string) || '',
    cashierPaymentMethodCode: (data?.cashierPaymentMethodCode as string) || '',
    cashierPaymentMethodName: (data?.cashierPaymentMethodName as string) || '',
    chargebackState: (data?.chargebackState as CHARGEBACK_STATE) || null,
    clientAccountId: (data?.clientAccountId as string) || '',
    conditionalCardholder: (data?.conditionalCardholder as string) || '',
    created: (data?.created as string) || '',
    cryptoAmount: data?.cryptoAmount as number,
    cryptoCurrency: (data?.cryptoCurrency as string) || '',
    currency: (data?.currency as string) || '',
    currencyRate: data?.currencyRate as number,
    customerAccessToken: (data?.customerAccessToken as string) || '',
    customerCountry: (data?.customerCountry as string) || '',
    customerEmail: (data?.customerEmail as string) || '',
    customerIp: (data?.customerIp as string) || '',
    customerPersonalId: (data?.customerPersonalId as string) || '',
    customerPhone: (data?.customerPhone as string) || '',
    customerPhoneCountryCode: (data?.customerPhoneCountryCode as string) || '',
    customerTaxNumber: (data?.customerTaxNumber as string) || '',
    depositType: (data?.depositType as string) || '',
    deviceType: (data?.deviceType as string) || '',
    id: (data?.id as string) || '',
    initialAmount: (data?.initialAmount as number) || 0,
    legalEntity: (data?.legalEntity as string) || '',
    merchantCustomerId: (data?.merchantCustomerId as string) || '',
    merchantId: (data?.merchantId as number) || 0,
    merchantName: (data?.merchantName as string) || '',
    merchantTerminalId: (data?.merchantTerminalId as number) || 0,
    merchantTerminalName: (data?.merchantTerminalName as string) || '',
    merchantTransactionId: (data?.merchantTransactionId as string) || '',
    metadata: (data?.metadata as string) || '',
    moto: !!data?.moto,
    mt4Id: (data?.mt4Id as string) || '',
    operatingSystem: (data?.operatingSystem as string) || '',
    original: !!data?.original,
    originCardholder: (data?.originCardholder as string) || '',
    paymentLinkId: (data?.paymentLinkId as number) || 0,
    paymentMethod: (data?.paymentMethod as string) || '',
    paymentMethodChanged: !!data?.paymentMethodChanged,
    prepareFieldsRequests: (data?.prepareFieldsRequests as string) || '',
    promoCode: (data?.promoCode as string) || '',
    providerAmount: data?.providerAmount as number,
    providerCurrency: (data?.providerCurrency as string) || '',
    pspCheckoutMethod: (data?.pspCheckoutMethod as string) || '',
    pspExecutionId: (data?.pspExecutionId as string) || '',
    reconciliationStatus: (data?.reconciliationStatus as string) || '',
    refTransactionId: (data?.refTransactionId as string) || '',
    refundedAmount: data?.refundedAmount as number,
    resultCode: (data?.resultCode as string) || '',
    routingRulesetName: (data?.routingRulesetName as string) || '',
    routingSchemaName: (data?.routingSchemaName as string) || '',
    state: (data?.state as TRANSACTIONS_STATE) || '',
    terminalLinkDescription: (data?.terminalLinkDescription as string) || '',
    terminalLinkId: (data?.terminalLinkId as number) || 0,
    tokenPresence: !!data?.tokenPresence,
    transactionCountry: (data?.transactionCountry as string) || '',
    type: (data?.type as TRANSACTIONS_TYPE) || '',
    updated: (data?.updated as string) || '',
    userBankMessage: (data?.userBankMessage as string) || '',
    userIp: (data?.userIp as string) || '',
    userName: (data?.userName as string) || '',
  };
};

export const normalizeTransactionCard = (data: TObject): ITransactionCard => {
  return {
    binCountryCode: (data?.binCountryCode as string) || '',
    binCountryName: (data?.binCountryName as string) || '',
    cardBin: (data?.cardBin as string) || '',
    cardBrand: (data?.cardBrand as string) || '',
    cardCategory: (data?.cardCategory as string) || '',
    cardFullBin: (data?.cardFullBin as string) || '',
    cardFullBinLength: (data?.cardFullBinLength as number) || null,
    cardholder: (data?.cardholder as string) || '',
    cardIssuingOrganization: (data?.cardIssuingOrganization as string) || '',
    cardLast4: (data?.cardLast4 as string) || '',
    cardPanId: (data?.cardPanId as number) || 0,
    cardType: (data?.cardType as string) || '',
    conditionalCardholder: (data?.conditionalCardholder as string) || '',
    expireMonth: (data?.expireMonth as number) || 0,
    expireYear: (data?.expireYear as number) || 0,
    id: (data?.id as string) || '',
    originCardholder: (data?.originCardholder as string) || '',
  };
};

export const normalizeTransactionCustomer = (data: TObject): ITransactionCustomer => {
  return {
    accountExternalSystemId: (data?.accountExternalSystemId as string) || '',
    addressLine1: (data?.addressLine1 as string) || '',
    addressLine2: (data?.addressLine2 as string) || '',
    bankCode: (data?.bankCode as string) || '',
    bankName: (data?.bankName as string) || '',
    city: (data?.city as string) || '',
    country: (data?.country as string) || '',
    customerAccessToken: (data?.customerAccessToken as string) || '',
    customerCountry: (data?.customerCountry as string) || '',
    customerId: (data?.customerId as string) || '',
    customerPersonalId: (data?.customerPersonalId as string) || '',
    customerTaxNumber: (data?.customerTaxNumber as string) || '',
    dateOfBirth: (data?.dateOfBirth as string) || '',
    deviceFingerprint: (data?.deviceFingerprint as string) || '',
    email: (data?.email as string) || '',
    firstName: (data?.firstName as string) || '',
    id: (data?.id as string) || '',
    ipAddress: (data?.ipAddress as string) || '',
    lastName: (data?.lastName as string) || '',
    locale: (data?.locale as string) || '',
    merchantCustomerId: (data?.merchantCustomerId as string) || '',
    phone: (data?.phone as string) || '',
    phoneCountryCode: (data?.phoneCountryCode as string) || '',
    postalCode: (data?.postalCode as string) || '',
    registrationDate: (data?.registrationDate as string) || '',
    state: (data?.state as string) || '',
    userAgent: (data?.userAgent as string) || '',
  };
};

export const normalizeTransactionDetails = (
  data: ITransactionDetailsResponse,
): ITransactionDetails => {
  return {
    amount: {
      amount: data?.amountDetailsBlock?.amount as number,
      baseCurrencyAmount: data?.amountDetailsBlock?.baseCurrencyAmount as number,
      cryptoAmount: data?.amountDetailsBlock?.cryptoAmount as number,
      cryptoCurrency: (data?.amountDetailsBlock?.cryptoCurrency as string) || '',
      currencyRate: data?.amountDetailsBlock?.currencyRate as number,
      fee: data?.amountDetailsBlock?.fee as number,
      initialAmount: data?.amountDetailsBlock?.initialAmount as number,
      initialCurrency: (data?.amountDetailsBlock?.initialCurrency as string) || '',
      initialFee: data?.amountDetailsBlock?.initialFee as number,
      processedAmount: data?.amountDetailsBlock?.processedAmount as number,
      processedCurrency: (data?.amountDetailsBlock?.processedCurrency as string) || '',
      refundedAmount: data?.amountDetailsBlock?.refundedAmount as number,
    },
    basic: {
      antifraudProvider: (data?.basicBlock?.antifraudProvider as string) || 'NONE',
      antifraudRiskScore: data?.basicBlock?.antifraudRiskScore as number,
      antifraudRiskStatus: (data?.basicBlock?.antifraudRiskStatus as string) || null,
      bankTID: (data?.basicBlock?.bankTID as string) || '',
      browserName: (data?.basicBlock?.browserName as string) || '',
      browserVersion: (data?.basicBlock?.browserVersion as string) || '',
      cascaded: !!data?.basicBlock?.cascaded,
      cashierPaymentMethodCode:
        (data?.basicBlock?.cashierPaymentMethodCode as string) || '',
      cashierPaymentMethodName:
        (data?.basicBlock?.cashierPaymentMethodName as string) || '',
      chargebackState: (data?.basicBlock?.chargebackState as string) || '',
      currency: (data?.basicBlock?.currency as string) || '',
      depositType: (data?.basicBlock?.depositType as string) || '',
      description: (data?.basicBlock?.description as string) || '',
      deviceFingerprint: (data?.basicBlock?.deviceFingerprint as string) || null,
      deviceType: (data?.basicBlock?.deviceType as string) || '',
      executionId: (data?.basicBlock?.executionId as string) || '',
      id: (data?.basicBlock?.id as string) || '',
      isMoto: !!data?.basicBlock?.isMoto,
      isRefundable: !!data?.basicBlock?.isRefundable,
      merchantTerminalId: (data?.basicBlock?.merchantTerminalId as number) || 0,
      merchantTerminalName: (data?.basicBlock?.merchantTerminalName as string) || '',
      merchantTransactionId: (data?.basicBlock.merchantTransactionId as string) || '',
      metadata: (data?.basicBlock?.metadata as string) || '',
      mt4id: (data?.basicBlock.mt4id as string) || '',
      operatingSystem: (data?.basicBlock?.operatingSystem as string) || '',
      paymentLinkId: (data?.basicBlock?.paymentLinkId as number) || 0,
      paymentMethod: (data?.basicBlock?.paymentMethod as string) || '',
      paymentMethodChanged: !!data?.basicBlock?.paymentMethodChanged,
      prepareFieldsRequests: (data?.basicBlock?.prepareFieldsRequests as string) || '',
      promoCode: (data?.basicBlock?.promoCode as string) || '',
      pspCheckoutMethod: (data?.basicBlock?.pspCheckoutMethod as string) || '',
      pspExecutionId: (data?.basicBlock?.pspExecutionId as string) || '',
      pspId: (data?.basicBlock?.pspId as number) || 0,
      pspName: (data?.basicBlock?.pspName as string) || '',
      pspTerminalId: (data?.basicBlock?.pspTerminalId as number) || 0,
      pspTerminalName: (data?.basicBlock?.pspTerminalName as string) || '',
      reconciliationStatus: (data?.basicBlock?.reconciliationStatus as string) || '',
      referenceId: (data?.basicBlock?.referenceId as string) || '',
      resolution: (data?.basicBlock?.resolution as string) || '',
      resultCode: (data?.basicBlock?.resultCode as string) || '',
      source: !!data?.basicBlock?.source,
      status: (data?.basicBlock?.status as TRANSACTIONS_STATE) || '',
      transactionCountry: (data?.basicBlock?.transactionCountry as string) || '',
      type: (data?.basicBlock?.type as TRANSACTIONS_TYPE) || '',
      userBankMessage: (data?.basicBlock?.userBankMessage as string) || '',
    },
    rdp: {
      cascadingSchemeId: (data?.rdpBlock?.cascadingSchemeId as number) || 0,
      cascadingSchemeName: (data?.rdpBlock?.cascadingSchemeName as string) || '',
      routingRulesetId: (data?.rdpBlock?.routingRulesetId as number) || 0,
      routingRulesetName: (data?.rdpBlock?.routingRulesetName as string) || '',
      routingSchemeId: (data?.rdpBlock?.routingSchemeId as number) || 0,
      routingSchemeName: (data?.rdpBlock?.routingSchemeName as string) || '',
      terminalLinkDescription: (data?.rdpBlock?.terminalLinkDescription as string) || '',
      terminalLinkId: (data?.rdpBlock?.terminalLinkId as number) || 0,
    },
    recon: {
      comment: (data?.reconBlock?.comment as string) || '',
      csvAmount: (data?.reconBlock?.csvAmount as number) || 0,
      csvCurrency: (data?.reconBlock?.csvCurrency as string) || '',
      csvState: (data?.reconBlock?.csvState as string) || '',
      fileId: (data?.reconBlock?.fileId as number) || 0,
      folderId: (data?.reconBlock?.folderId as number) || 0,
      reconciliationStatus: (data?.reconBlock?.reconciliationStatus as string) || '',
      resolveDate: (data?.reconBlock?.resolveDate as string) || '',
      userEmail: (data?.reconBlock?.userEmail as string) || '',
    },
    timeline: {
      createdAt: (data?.timelineBlock?.created as string) || '',
      updatedAt: (data?.timelineBlock?.updated as string) || '',
    },
  };
};

const normalizeRequest = (data: TObject) => {
  if (!data) {
    return null;
  }

  return {
    date: (data?.date as string) || '',
    headers: (data?.headers as TObject) || {},
    httpMethod: (data?.httpMethod as string) || '',
    payload: (data?.payload as string) || '',
    plainPayload: (data?.plainPayload as string) || null,
    url: (data?.url as string) || '',
  };
};

const normalizeResponse = (data: TObject) => {
  if (!data) {
    return null;
  }

  return {
    date: (data?.date as string) || '',
    httpStatus: (data?.httpStatus as number) || 0,
    payload: (data?.payload as string) || '',
    plainPayload: (data?.plainPayload as string) || null,
  };
};

const normalizeAdditionalInfo = (data: { parameters: TObject }) => {
  if (!data) {
    return null;
  }

  return {
    parameters: {
      adapterCode: (data?.parameters?.adapterCode as string) || '',
      rulesetId: (data?.parameters?.rulesetId as string) || '',
      rulesetName: (data?.parameters?.rulesetName as string) || '',
      terminalLinkId: (data?.parameters?.terminalLinkId as string) || '',
    },
  };
};

type IDetailData = TObject & {
  additionalInfo: { parameters: TObject };
  request: TObject;
  response: TObject;
};

export const normalizedExtraDetails = (
  extraDetails: unknown,
): INullable<IExtraDetails[]> => {
  if (typeof extraDetails !== 'string') {
    return null;
  }

  let parsedDetails;
  try {
    parsedDetails = JSON.parse(extraDetails);
  } catch {
    // eslint-disable-next-line no-console
    console.warn(`Can't parse JSON`);
    return null;
  }

  return parsedDetails.map((detail: IDetailData) => {
    return {
      additionalInfo: normalizeAdditionalInfo(detail?.additionalInfo),
      request: normalizeRequest(detail.request),
      response: normalizeResponse(detail.response),
      type: (detail?.type as EXTRA_DETAILS_TYPE) || '',
    };
  });
};

const normalizeOperationHistory = (data: TObject): IOperationHistory => {
  return {
    bankMessage: (data?.bankMessage as string) || '',
    created: (data?.created as string) || '',
    description: (data?.description as string) || '',
    details: (data?.details as string) || '',
    executionId: (data?.executionId as string) || '',
    executionTime: (data?.executionTime as string) || '',
    extraDetails: normalizedExtraDetails(data?.extraDetails),
    id: (data?.id as number) || 0,
    operationState: (data?.operationState as OPERATION_STATE) || '',
    operationType: (data?.operationType as string) || '',
    resultCode: (data?.resultCode as string) || '',
    transactionState: (data?.transactionState as TRANSACTIONS_STATE) || '',
  };
};

export const normalizeTransactionOperationHistory = (
  data: ITransactionOperationHistoryData,
): ITransactionOperationHistory => {
  return {
    operationHistory: data?.operationsList?.map(normalizeOperationHistory) || [],
    transactionId: (data?.transactionId as string) || '',
  };
};

const normalizerWebhooks = (data: TObject): IWebhook => {
  return {
    body: (data?.body as string) || '',
    id: (data?.id as number) || 0,
    lastResponseBody: (data?.lastResponseBody as string) || '',
    lastResponseCode: (data?.lastResponseCode as number) || 0,
    lastStartDate: (data?.lastStartDate as string) || '',
    nextRunDate: (data?.nextRunDate as string) || '',
    runCount: (data?.runCount as number) || 0,
    startDate: (data?.startDate as string) || '',
    url: (data?.url as string) || '',
  };
};

export const normalizeTransactionWebhooks = (
  data: ITransactionWebhooksData,
): ITransactionWebhooks => {
  return {
    listWebhooks: data?.listWebhooks?.map(normalizerWebhooks) || [],
    transactionId: (data?.transactionId as string) || '',
  };
};

const normalizerCascading = (data: TObject): ICascading => {
  return {
    id: (data?.id as string) || '',
    order: (data?.order as number) || 0,
    state: (data?.state as string) || '',
    updated: (data?.updated as string) || '',
  };
};

export const normalizeTransactionCascading = (
  data: ITransactionCascadingData,
): ITransactionCascading => {
  return {
    cascadedTransactions: data?.cascadedTransactions?.map(normalizerCascading) || [],
    cascadingSchemaName: data?.cascadingSchemaName as string,
    cascadingSchemaPriorityType: data?.cascadingSchemaPriorityType as string,
    cascadingSchemaRetries: data?.cascadingSchemaRetries as number,
    id: (data?.id as string) || '',
  };
};

const normalizeRuleset = (data: TObject): IRuleset => {
  return {
    id: (data?.id as number) || 0,
    name: (data?.name as string) || '',
  };
};

const normalizeCategorizedRuleset = (data: TObject): ICategorizedRuleset => {
  return {
    clientPriority: !!data?.clientPriority,
    id: (data?.id as number) || 0,
    name: (data?.name as string) || '',
    priority: (data?.priority as number) || 0,
    ratio: (data?.ratio as number) || 0,
    remainingTimerPeriod: (data?.remainingTimerPeriod as number) || null,
    schemePriority: (data?.schemePriority as number) ?? null,
  };
};

export const normalizeRoutingHistory = (data: IRoutingHistoryData): IRoutingHistory => {
  const categorizedRulesets: Record<string, ICategorizedRuleset[]> = {};
  Object.keys(data?.categorizedRulesets || {}).forEach((key) => {
    categorizedRulesets[key] = data?.categorizedRulesets[key].map(
      normalizeCategorizedRuleset,
    );
  });

  return {
    actionType: (data?.actionType as string) || '',
    categorizedRulesets,
    createdDate: (data?.createdDate as string) || '',
    rulesets: data?.rulesets?.map(normalizeRuleset) || [],
    transactionId: (data?.transactionId as string) || '',
    value: (data?.value as string) || '',
  };
};

export const normalizeTransactionRoutingHistory = (
  data: ITransactionRoutingHistoryData,
): ITransactionRoutingHistory => {
  return {
    routingHistories: data?.routingHistories?.map(normalizeRoutingHistory) || [],
  };
};

export const normalizeEditData = <T, R>(data: R): Partial<T> => {
  const payload: TObject = {};
  Object.entries(data as TObject).forEach(([key, value]) => {
    if (value || typeof value === 'boolean') payload[key] = value;
  });
  return payload as T;
};

export const normalizeChargebackStatistics = (data: TObject): IChargebackStatistics => {
  return {
    chargebackPercentage: (data?.chargebackPercentage as number) || 0,
    chargedBackTxCount: (data?.chargedBackTxCount as number) || 0,
    completedTxCount: (data?.completedTxCount as number) || 0,
    merchantTerminalId: (data?.merchantTerminalId as number) || 0,
  };
};

// Parses the JSON-serialized List<Set<String>> from the BE into rounds of field keys.
// Outer list = Prepare Page rounds (preserves chronological order); inner list = field
// keys requested in that round (preserves the within-round order set by the BE's
// LinkedHashSet). Empty / non-string entries are dropped; rounds that become empty
// are dropped too. Returns [] on missing / malformed input.
export const parsePrepareFieldsRequests = (raw: string): string[][] => {
  if (!raw) {
    return [];
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }

  if (!Array.isArray(parsed)) {
    return [];
  }

  const rounds: string[][] = [];
  parsed.forEach((round) => {
    if (!Array.isArray(round)) {
      return;
    }
    const keys = round.filter((key): key is string => typeof key === 'string' && !!key);
    if (keys.length) {
      rounds.push(keys);
    }
  });

  return rounds;
};
